import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Play, Pause, SkipBack, SkipForward, FlipHorizontal2, Maximize, Minimize,
  Plus, Pencil, Check, CheckCircle2, Circle, Trash2, Loader2, X, ScrollText,
  Radio as RadioIcon, Zap, Sparkles, ClipboardList, Download, Mail, MonitorSmartphone, Lock, AudioLines,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import PageHeader from "@/components/PageHeader";
import AiWriteScriptModal, { AiWriteButton } from "@/components/AiWriteScriptModal";
import { WordSyncDisplay, estimateWordTimings, linesFromText, totalDurationMs } from "@/components/WordSyncDisplay";

const SOURCE_TAGS = {
  news_studio:        { label: "News Studio",     cls: "text-violet-300 border-violet-500/40 bg-violet-500/10" },
  ai_dj_assistant:    { label: "AI DJ Assistant", cls: "text-cyan-300 border-cyan-500/40 bg-cyan-500/10" },
  commercial_creator: { label: "Commercials",     cls: "text-rose-300 border-rose-500/40 bg-rose-500/10" },
  request_line:       { label: "Request Line",    cls: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" },
  writers_desk:       { label: "Editorial Desk",  cls: "text-sky-300 border-sky-500/40 bg-sky-500/10" },
  manual:             { label: "Manual",          cls: "text-ink-secondary border-line bg-bg-elevated" },
};

const fmtDur = (s) => `${Math.floor((s || 0) / 60)}:${String((s || 0) % 60).padStart(2, "0")}`;
const escapeRx = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

function renderScript(text, stationName) {
  if (!text) return null;
  const rx = stationName
    ? new RegExp(`(\\*\\*[^*]+\\*\\*|${escapeRx(stationName)})`, "gi")
    : /(\*\*[^*]+\*\*)/gi;
  return text.split(rx).map((seg, i) => {
    if (!seg) return null;
    if (seg.startsWith("**") && seg.endsWith("**"))
      return <span key={i} className="text-brand-amber">{seg.slice(2, -2)}</span>;
    if (stationName && seg.toLowerCase() === stationName.toLowerCase())
      return <span key={i} className="text-brand-amber">{seg}</span>;
    return <React.Fragment key={i}>{seg}</React.Fragment>;
  });
}

export default function Prompter() {
  const { user } = useAuth();
  const [rundowns, setRundowns] = useState([]);
  const [rd, setRd] = useState(null);
  const [segs, setSegs] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stationName, setStationName] = useState("");
  const [nowPlaying, setNowPlaying] = useState(null);
  const [popAllowed, setPopAllowed] = useState(true);

  // prompter controls
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(4);
  const [fontSize, setFontSize] = useState(34);
  const [mirrored, setMirrored] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [clock, setClock] = useState(new Date());
  const [elapsed, setElapsed] = useState(0);

  // edit + add dialogs
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [addAiStart, setAddAiStart] = useState(false);
  const [showLogOpen, setShowLogOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const [followMode, setFollowMode] = useState(searchParams.get("follow") === "1");

  // word-sync mode
  const [wordSync, setWordSync] = useState(false);
  const [syncMs, setSyncMs] = useState(0);

  const scrollRef = useRef(null);
  const panelRef = useRef(null);
  const speedRef = useRef(speed);
  speedRef.current = speed;

  const active = useMemo(() => segs.find((s) => s.segment_id === activeId) || null, [segs, activeId]);
  const totalSeconds = useMemo(() => segs.reduce((a, s) => a + (s.duration_estimate_seconds || 0), 0), [segs]);

  const syncLines = useMemo(() => {
    if (!active?.script_text) return [];
    const lines = linesFromText(active.script_text.replace(/\*\*/g, ""));
    return estimateWordTimings(lines, { durationMs: ((active.duration_estimate_seconds || 0) * 1000) || undefined });
  }, [active]);
  const syncTotalMs = useMemo(() => totalDurationMs(syncLines), [syncLines]);

  // ── data loading ──
  const openRundown = useCallback(async (id) => {
    const { data } = await api.get(`/prompter/rundowns/${id}`);
    setRd(data.rundown);
    setSegs(data.segments);
    const act = data.segments.find((s) => s.status === "active") || data.segments[0];
    setActiveId(act?.segment_id || null);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [{ data }, st] = await Promise.all([
          api.get("/prompter/rundowns"),
          api.get("/stations/me").catch(() => null),
        ]);
        setRundowns(data.rundowns);
        setPopAllowed(data.proof_of_play_allowed !== false);
        if (st?.data?.name) setStationName(st.data.name);
        const first = data.rundowns.find((r) => r.active) || data.rundowns[0];
        if (first) await openRundown(first.rundown_id);
      } catch { toast.error("Could not load rundowns"); }
      finally { setLoading(false); }
    })();
  }, [openRundown]);

  // ── clock + elapsed + now playing ──
  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    setElapsed(0);
    setSyncMs(0);
    if (!activeId) return;
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, [activeId]);

  useEffect(() => {
    if (!user?.station_id) return;
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get(`/public/nowplaying/${user.station_id}`);
        if (alive && data?.ok && data.track) setNowPlaying(data.track);
      } catch { /* silent */ }
    };
    load();
    const t = setInterval(load, 30000);
    return () => { alive = false; clearInterval(t); };
  }, [user?.station_id]);

  // ── second-screen follow mode: poll the station's live state every 2s ──
  useEffect(() => {
    if (!followMode) return;
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get("/prompter/sync");
        if (!alive || !data.rundown) return;
        setRd(data.rundown);
        setSegs(data.segments);
        setActiveId((cur) => {
          const next = data.active_segment_id || cur;
          if (next !== cur && scrollRef.current) scrollRef.current.scrollTop = 0;
          return next;
        });
      } catch { /* keep last state */ }
    };
    tick();
    const t = setInterval(tick, 2000);
    return () => { alive = false; clearInterval(t); };
  }, [followMode]);

  // ── auto-scroll loop ──
  useEffect(() => {
    if (!playing || wordSync) return;
    let raf, last = performance.now();
    const step = (t) => {
      const dt = t - last; last = t;
      const el = scrollRef.current;
      if (el) {
        el.scrollTop += (speedRef.current * 18 * dt) / 1000;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 2) { setPlaying(false); return; }
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [playing, wordSync]);

  // ── word-sync clock (speed 4 = 1x pace) ──
  useEffect(() => {
    if (!playing || !wordSync) return;
    let raf, last = performance.now();
    const step = (t) => {
      const dt = (t - last) * (speedRef.current / 4);
      last = t;
      let done = false;
      setSyncMs((ms) => {
        const next = ms + dt;
        if (next >= syncTotalMs) { done = true; return syncTotalMs; }
        return next;
      });
      if (done) { setPlaying(false); return; }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [playing, wordSync, syncTotalMs]);

  // ── fullscreen ──
  useEffect(() => {
    const onFs = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);
  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else panelRef.current?.requestFullscreen?.();
  };

  // ── segment ops ──
  const patchSeg = async (id, patch) => {
    const { data } = await api.patch(`/prompter/segments/${id}`, patch);
    setSegs((list) => list.map((s) => {
      if (s.segment_id === id) return data;
      if (patch.status === "active" && s.status === "active") return { ...s, status: "pending" };
      return s;
    }));
    return data;
  };

  const selectSegment = async (seg) => {
    if (followMode) return;
    setPlaying(false); setEditing(false);
    setActiveId(seg.segment_id);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    try { await patchSeg(seg.segment_id, { status: "active" }); } catch { /* keep local */ }
  };

  const idxOf = (id) => segs.findIndex((s) => s.segment_id === id);
  const nextSegment = async () => {
    if (followMode) return;
    const i = idxOf(activeId);
    if (i < 0) return;
    try { await patchSeg(activeId, { status: "done" }); } catch { /* noop */ }
    const nxt = segs[i + 1];
    if (nxt) await selectSegment(nxt);
    else { setPlaying(false); toast.success("End of rundown — great show!"); }
  };
  const prevSegment = async () => {
    if (followMode) return;
    if (wordSync && syncMs > 1500) { setPlaying(false); setSyncMs(0); return; }
    const el = scrollRef.current;
    if (el && el.scrollTop > 40) {
      setPlaying(false);
      el.scrollTop = 0;
      return;
    }
    const i = idxOf(activeId);
    if (i > 0) await selectSegment(segs[i - 1]);
    else toast("Already at the top of the rundown");
  };

  const startEdit = () => { if (followMode) return; if (active) { setEditText(active.script_text); setEditing(true); setPlaying(false); } };
  const saveEdit = async () => {
    try {
      await patchSeg(activeId, { script_text: editText });
      setEditing(false);
      toast.success("Script updated");
    } catch { toast.error("Could not save"); }
  };

  const deleteSegment = async (seg) => {
    try {
      await api.delete(`/prompter/segments/${seg.segment_id}`);
      setSegs((l) => l.filter((s) => s.segment_id !== seg.segment_id));
      if (activeId === seg.segment_id) setActiveId(null);
    } catch { toast.error("Could not delete segment"); }
  };

  const createRundown = async () => {
    try {
      const { data } = await api.post("/prompter/rundowns", { title: "" });
      setRundowns((r) => [{ ...data, segment_count: 0, total_seconds: 0 }, ...r.map((x) => ({ ...x, active: false }))]);
      await openRundown(data.rundown_id);
    } catch { toast.error("Could not create rundown"); }
  };

  const [loadingToday, setLoadingToday] = useState(false);
  const loadToday = async () => {
    setLoadingToday(true);
    try {
      const { data } = await api.post("/prompter/load-today", { hour: new Date().getHours() });
      const list = (await api.get("/prompter/rundowns")).data.rundowns;
      setRundowns(list);
      await openRundown(data.rundown_id);
      toast.success(
        `Built "${data.title}" — ${data.segments.length} segment${data.segments.length === 1 ? "" : "s"}` +
        (data.skipped.length ? ` (nothing found for: ${data.skipped.join(", ")})` : "")
      );
    } catch { toast.error("Could not build today's show"); }
    finally { setLoadingToday(false); }
  };

  const addSegment = async (title, text, source = "manual") => {
    if (!rd) return;
    const seg = (await api.post(`/prompter/rundowns/${rd.rundown_id}/segments`,
      { title, script_text: text, source_module: source })).data;
    setSegs((l) => [...l, seg]);
    if (!activeId) setActiveId(seg.segment_id);
    setAddOpen(false);
  };

  if (loading) return <div className="p-10 font-mono text-xs text-ink-muted">LOADING THE PROMPTER...</div>;

  return (
    <div className="p-6 md:p-10 max-w-[1500px] mx-auto animate-fade-up" data-testid="prompter-page">
      <PageHeader
        eyebrow="// THE PROMPTER"
        title="Read it like you wrote it."
        description="Your rundown on the left, a broadcast-grade teleprompter on the right. Scripts flow in from every studio — and you can always edit before you speak."
      />

      {!rd ? (
        <div className="rounded-xl border border-dashed border-line bg-bg-surface p-12 text-center" data-testid="prompter-empty">
          <ScrollText className="h-10 w-10 mx-auto text-ink-muted mb-4" />
          <div className="font-display text-xl font-bold mb-2">No rundown yet</div>
          <div className="text-sm text-ink-secondary mb-6">Create your first rundown, then send scripts here from News Studio, AI DJ Assistant, Commercials or Request Line.</div>
          <div className="flex items-center justify-center gap-3">
            <button onClick={loadToday} disabled={loadingToday} data-testid="load-today-btn"
              className="inline-flex items-center gap-1.5 rounded-md bg-brand-amber px-5 py-2.5 font-semibold text-black amber-glow disabled:opacity-50">
              {loadingToday ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />} Load today's show
            </button>
            <button onClick={createRundown} data-testid="rundown-new-btn"
              className="rounded-md border border-line bg-bg-elevated px-5 py-2.5 font-semibold text-ink-secondary hover:text-brand-amber transition">
              Blank rundown
            </button>
          </div>
        </div>
      ) : (
        <div className="grid lg:grid-cols-[330px,1fr] gap-4">
          {/* ── LEFT: rundown queue ── */}
          <div className="rounded-xl border border-line bg-bg-surface p-4 self-start" data-testid="rundown-queue">
            <div className="flex items-center gap-2 mb-3">
              <select
                value={rd.rundown_id}
                onChange={(e) => openRundown(e.target.value)}
                data-testid="rundown-select"
                className="flex-1 min-w-0 rounded-md border border-line bg-bg-elevated px-2 py-1.5 text-sm font-semibold outline-none"
              >
                {rundowns.map((r) => (
                  <option key={r.rundown_id} value={r.rundown_id}>{r.title}</option>
                ))}
              </select>
              <button onClick={createRundown} data-testid="rundown-new-btn" title="New rundown"
                className="shrink-0 rounded-md border border-line bg-bg-elevated p-2 text-ink-secondary hover:text-brand-amber transition">
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <button onClick={loadToday} disabled={loadingToday} data-testid="load-today-btn"
              className="mb-3 w-full inline-flex items-center justify-center gap-1.5 rounded-lg border border-brand-amber/50 bg-brand-amber/10 py-2 text-sm font-semibold text-brand-amber hover:bg-brand-amber/20 transition disabled:opacity-50"
              title="Auto-build a rundown: welcome, latest news bulletin, a commercial, and recent shoutouts">
              {loadingToday ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />} Load today's show
            </button>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-3" data-testid="rundown-total">
              {segs.length} segment{segs.length === 1 ? "" : "s"} · est {fmtDur(totalSeconds)}
            </div>

            <div className="flex flex-col gap-1.5">
              {segs.map((s) => {
                const tag = SOURCE_TAGS[s.source_module] || SOURCE_TAGS.manual;
                const isActive = s.segment_id === activeId;
                return (
                  <div
                    key={s.segment_id}
                    onClick={() => selectSegment(s)}
                    data-testid={`segment-item-${s.segment_id}`}
                    className={`group cursor-pointer rounded-lg border p-3 transition ${
                      isActive
                        ? "border-brand-amber bg-brand-amber/5"
                        : s.status === "done"
                        ? "border-line bg-bg-elevated opacity-45 hover:opacity-70"
                        : "border-line bg-bg-elevated hover:border-brand-amber/40"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {s.status === "done" ? (
                        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                      ) : isActive ? (
                        <Play className="h-3.5 w-3.5 text-brand-amber shrink-0" />
                      ) : (
                        <Circle className="h-3.5 w-3.5 text-ink-muted shrink-0" />
                      )}
                      <div className="flex-1 min-w-0 text-sm font-semibold truncate">{s.title}</div>
                      <span className="font-mono text-[10px] text-ink-muted shrink-0">{fmtDur(s.duration_estimate_seconds)}</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteSegment(s); }}
                        data-testid={`segment-delete-${s.segment_id}`}
                        className="opacity-0 group-hover:opacity-100 text-ink-muted hover:text-rose-400 transition shrink-0"
                        title="Remove segment"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                    <span className={`mt-1.5 ml-5 inline-block rounded border px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider ${tag.cls}`}>
                      {tag.label}
                    </span>
                  </div>
                );
              })}
            </div>

            <div className="mt-3 grid grid-cols-2 gap-2">
              <button onClick={() => { setAddAiStart(false); setAddOpen(true); }} data-testid="segment-add-btn"
                className="rounded-lg border border-dashed border-line py-2.5 text-sm text-ink-secondary hover:text-brand-amber hover:border-brand-amber/50 transition">
                + Add segment
              </button>
              <button onClick={() => { setAddAiStart(true); setAddOpen(true); }} data-testid="segment-ai-btn"
                title="Have AI write a script and drop it straight into your rundown"
                className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-violet-500/50 bg-violet-500/10 py-2.5 text-sm font-semibold text-violet-300 hover:bg-violet-500/20 transition">
                <Sparkles className="h-4 w-4" /> AI script
              </button>
            </div>
          </div>

          {/* ── RIGHT: teleprompter ── */}
          <div ref={panelRef} className="rounded-xl border border-line bg-bg-surface overflow-hidden flex flex-col" data-testid="prompter-panel">
            {/* Top bar */}
            <div className="flex items-center justify-between gap-4 border-b border-line px-5 py-3">
              <div className="font-mono text-2xl font-bold tabular-nums tracking-tight" data-testid="prompter-clock">
                {clock.toLocaleTimeString()}
              </div>
              <div className="text-center">
                <div className="font-mono text-lg font-bold text-emerald-400 tabular-nums" data-testid="prompter-elapsed">{fmtDur(elapsed)}</div>
                <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-ink-muted">segment elapsed</div>
              </div>
              <div className="flex items-center gap-3 min-w-0">
                {nowPlaying && (
                  <div className="flex items-center gap-1.5 min-w-0 text-xs text-ink-secondary" data-testid="prompter-nowplaying">
                    <RadioIcon className="h-3 w-3 text-emerald-400 shrink-0" />
                    <span className="truncate">On air: “{nowPlaying.title}” — {nowPlaying.artist}</span>
                  </div>
                )}
                <button onClick={() => setFollowMode((f) => !f)} data-testid="follow-mode-btn"
                  title="Second-screen sync: open this page on a phone/tablet and turn this on — it follows the live rundown automatically (or open /dashboard/prompter?follow=1)"
                  className={`shrink-0 inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-semibold transition ${
                    followMode
                      ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-300"
                      : "border-line bg-bg-elevated text-ink-secondary hover:text-brand-amber"
                  }`}>
                  {followMode && <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />}
                  <MonitorSmartphone className="h-3 w-3" /> {followMode ? "Following live" : "Second screen"}
                </button>
                <button onClick={() => setShowLogOpen(true)} data-testid="show-log-btn"
                  title={popAllowed ? "Proof-of-play record: what was read and when" : "Proof-of-Play Reports — a Professional feature. Click to preview."}
                  className="shrink-0 inline-flex items-center gap-1.5 rounded-md border border-line bg-bg-elevated px-3 py-1.5 text-xs font-semibold text-ink-secondary hover:text-brand-amber transition">
                  <ClipboardList className="h-3 w-3" /> Show log
                  {!popAllowed && (
                    <span data-testid="show-log-pro-badge"
                      className="inline-flex items-center gap-0.5 rounded bg-brand-amber/15 px-1 py-0.5 text-[8px] font-mono uppercase tracking-wider text-brand-amber">
                      <Lock className="h-2.5 w-2.5" /> PRO
                    </span>
                  )}
                </button>
                <button onClick={startEdit} data-testid="prompter-edit-btn" disabled={!active || editing}
                  className="shrink-0 inline-flex items-center gap-1.5 rounded-md border border-line bg-bg-elevated px-3 py-1.5 text-xs font-semibold text-ink-secondary hover:text-brand-amber transition disabled:opacity-40">
                  <Pencil className="h-3 w-3" /> Edit current script
                </button>
              </div>
            </div>

            {/* Display */}
            {editing ? (
              <div className="bg-black p-6 flex flex-col" style={{ height: isFullscreen ? "calc(100vh - 130px)" : 460 }} data-testid="prompter-edit-mode">
                <textarea
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  data-testid="edit-textarea"
                  className="flex-1 w-full resize-none rounded-lg border border-line bg-[#0a0a0a] p-4 text-lg leading-relaxed text-white outline-none focus:border-brand-amber/60"
                />
                <div className="mt-3 flex justify-end gap-2">
                  <button onClick={() => setEditing(false)} data-testid="edit-cancel-btn"
                    className="rounded-md border border-line bg-bg-elevated px-4 py-2 text-sm font-semibold text-ink-secondary">Cancel</button>
                  <button onClick={saveEdit} data-testid="edit-save-btn"
                    className="inline-flex items-center gap-1.5 rounded-md bg-brand-amber px-4 py-2 text-sm font-semibold text-black">
                    <Check className="h-4 w-4" /> Save
                  </button>
                </div>
              </div>
            ) : wordSync ? (
              <div className="relative bg-black flex items-center justify-center" style={{ height: isFullscreen ? "calc(100vh - 130px)" : 460 }} data-testid="prompter-wordsync-mode">
                {active ? (
                  <WordSyncDisplay lines={syncLines} currentMs={syncMs} fontSize={fontSize} mirrored={mirrored} />
                ) : (
                  <span className="text-ink-muted text-2xl">Pick a segment from the rundown to start reading.</span>
                )}
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/5">
                  <div className="h-full bg-brand-amber/70 transition-[width] duration-200" data-testid="wordsync-progress"
                    style={{ width: syncTotalMs ? `${Math.min(100, (syncMs / syncTotalMs) * 100)}%` : "0%" }} />
                </div>
              </div>
            ) : (
              <div className="relative bg-black" style={{ height: isFullscreen ? "calc(100vh - 130px)" : 460 }}>
                {/* reading guide */}
                <div className="pointer-events-none absolute left-0 right-0 z-10 border-t border-brand-amber/25" style={{ top: "38%" }} />
                <div ref={scrollRef} className="h-full overflow-y-auto sidebar-scroll" data-testid="prompter-display">
                  <div
                    className="mx-auto max-w-4xl px-10 text-center font-semibold leading-snug text-white"
                    style={{ fontSize: `${fontSize}px`, paddingTop: isFullscreen ? "38vh" : 170, paddingBottom: isFullscreen ? "70vh" : 420, transform: mirrored ? "scaleX(-1)" : "none" }}
                  >
                    {active ? renderScript(active.script_text, stationName) : (
                      <span className="text-ink-muted text-2xl">Pick a segment from the rundown to start reading.</span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Bottom controls */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 border-t border-line px-5 py-3">
              <div className="flex items-center gap-2">
                <button onClick={prevSegment} data-testid="prompter-prev-btn" title="Rewind script / previous segment"
                  className="rounded-md border border-line bg-bg-elevated p-2 text-ink-secondary hover:text-brand-amber transition">
                  <SkipBack className="h-4 w-4" />
                </button>
                <button onClick={() => setPlaying((p) => !p)} data-testid="prompter-play-btn" disabled={!active || editing}
                  className="rounded-md bg-brand-amber p-2.5 text-black amber-glow disabled:opacity-40" title={playing ? "Pause auto-scroll" : "Start auto-scroll"}>
                  {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                </button>
                <button onClick={nextSegment} data-testid="prompter-next-btn" title="Mark done + next segment"
                  className="rounded-md border border-line bg-bg-elevated p-2 text-ink-secondary hover:text-brand-amber transition">
                  <SkipForward className="h-4 w-4" />
                </button>
              </div>
              <label className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-ink-muted">
                Speed
                <input type="range" min="1" max="10" value={speed} onChange={(e) => setSpeed(+e.target.value)}
                  data-testid="prompter-speed-slider" className="w-28 accent-[#f5a623]" />
              </label>
              <label className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-ink-muted">
                Text size
                <input type="range" min="22" max="64" value={fontSize} onChange={(e) => setFontSize(+e.target.value)}
                  data-testid="prompter-size-slider" className="w-28 accent-[#f5a623]" />
              </label>
              <div className="ml-auto flex items-center gap-2">
                <button onClick={() => { setWordSync((w) => !w); setSyncMs(0); setPlaying(false); }} data-testid="prompter-wordsync-btn"
                  title="Word-sync mode: karaoke-style word-by-word pacing based on the segment's estimated read time"
                  className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-2 text-xs font-semibold transition ${
                    wordSync ? "border-brand-amber text-brand-amber bg-brand-amber/10" : "border-line bg-bg-elevated text-ink-secondary hover:text-brand-amber"
                  }`}>
                  <AudioLines className="h-4 w-4" /> Word-sync
                </button>
                <button onClick={() => setMirrored((m) => !m)} data-testid="prompter-mirror-btn" title="Mirror/flip for teleprompter glass"
                  className={`rounded-md border p-2 transition ${mirrored ? "border-brand-amber text-brand-amber bg-brand-amber/10" : "border-line bg-bg-elevated text-ink-secondary hover:text-brand-amber"}`}>
                  <FlipHorizontal2 className="h-4 w-4" />
                </button>
                <button onClick={toggleFullscreen} data-testid="prompter-fullscreen-btn" title="Fullscreen"
                  className="rounded-md border border-line bg-bg-elevated p-2 text-ink-secondary hover:text-brand-amber transition">
                  {isFullscreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* second-screen note */}
      {rd && (
        <div className="mt-4 rounded-lg border border-line bg-bg-surface p-3 text-[11.5px] text-ink-secondary flex gap-2.5" data-testid="prompter-mirror-note">
          <ScrollText className="h-4 w-4 text-brand-amber shrink-0 mt-0.5" />
          <span>Tip: open this same page on a phone or tablet and prop it next to your mic — segment edits and additions appear on reload. Use <span className="text-brand-amber">**double asterisks**</span> in any script to highlight phrases in amber; your station name is highlighted automatically.</span>
        </div>
      )}

      {addOpen && <AddSegmentModal onClose={() => setAddOpen(false)} onAdd={addSegment} startWithAi={addAiStart} />}
      {showLogOpen && rd && <ShowLogModal rundown={rd} onClose={() => setShowLogOpen(false)} />}
    </div>
  );
}

function ShowLogModal({ rundown, onClose }) {
  const [data, setData] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const sendEmail = async () => {
    if (!email.includes("@")) { toast.error("Enter a valid email address"); return; }
    setSending(true);
    try {
      await api.post(`/prompter/rundowns/${rundown.rundown_id}/email-log`, { to_email: email });
      toast.success(`Show log sent to ${email}`);
      setEmail("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send the email"); }
    finally { setSending(false); }
  };

  useEffect(() => {
    api.get(`/prompter/rundowns/${rundown.rundown_id}/show-log`)
      .then((r) => setData(r.data))
      .catch(() => setData({ entries: [], total_read_seconds: 0 }));
  }, [rundown.rundown_id]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const r = await api.get(`/prompter/rundowns/${rundown.rundown_id}/show-log.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `show-log-${(rundown.title || "rundown").replace(/[^\w-]+/g, "-")}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not export the show log"); }
    finally { setExporting(false); }
  };

  const fmtT = (iso) => (iso ? new Date(iso).toLocaleTimeString() : "—");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="show-log-modal">
      <div className="w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-xl border border-line bg-bg-surface p-6">
        <div className="flex items-center justify-between mb-1">
          <div className="font-display font-bold text-lg">Show log — {rundown.title}</div>
          <button onClick={onClose} data-testid="show-log-close"
            className="w-7 h-7 rounded-md flex items-center justify-center border border-line text-ink-muted hover:text-ink-primary transition">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="text-xs text-ink-secondary mb-4">
          Proof-of-play record — every segment start and finish is timestamped as you read. Export it for compliance files or sponsor receipts.
        </div>

        {data === null ? (
          <div className="text-xs text-ink-muted py-6"><Loader2 className="h-3.5 w-3.5 animate-spin inline mr-1.5" />Loading log...</div>
        ) : data.entries.length === 0 ? (
          <div className="text-sm text-ink-muted py-8 text-center border border-dashed border-line rounded-lg" data-testid="show-log-empty">
            Nothing logged yet — entries appear as you read segments (select → next) on air.
          </div>
        ) : (
          <div className="rounded-lg border border-line overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-bg-elevated text-[10px] font-mono uppercase tracking-wider text-ink-muted">
                  <th className="text-left px-3 py-2">Segment</th>
                  <th className="text-left px-3 py-2">Source</th>
                  <th className="text-left px-3 py-2">Started</th>
                  <th className="text-left px-3 py-2">Finished</th>
                  <th className="text-right px-3 py-2">Read time</th>
                </tr>
              </thead>
              <tbody>
                {data.entries.map((e, i) => {
                  const tag = SOURCE_TAGS[e.source_module] || SOURCE_TAGS.manual;
                  return (
                    <tr key={i} className="border-t border-line" data-testid={`show-log-entry-${i}`}>
                      <td className="px-3 py-2 font-semibold">{e.title}</td>
                      <td className="px-3 py-2">
                        <span className={`rounded border px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider ${tag.cls}`}>{tag.label}</span>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-ink-secondary">{fmtT(e.started_at)}</td>
                      <td className="px-3 py-2 font-mono text-xs text-ink-secondary">{fmtT(e.completed_at)}</td>
                      <td className="px-3 py-2 text-right font-mono text-xs">{e.read_seconds != null ? fmtDur(e.read_seconds) : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {data && data.export_allowed === false ? (
          <div className="mt-4 rounded-lg border border-brand-amber/40 bg-brand-amber/5 p-4" data-testid="show-log-upgrade">
            <div className="flex items-center gap-2 text-sm font-semibold mb-1">
              <Lock className="h-4 w-4 text-brand-amber" /> Proof-of-Play Reports is a Professional feature
            </div>
            <div className="text-xs text-ink-secondary mb-3">
              Upgrade to export this log as CSV or email sponsor receipts. Viewing the live log stays free on every plan.
            </div>
            <a href="/dashboard/billing?tab=plan" data-testid="show-log-upgrade-link"
              onClick={() => api.post("/upgrade-interest", { feature: "proof_of_play", source: "show_log_modal" }).catch(() => {})}
              className="inline-flex items-center gap-1.5 rounded-md bg-brand-amber px-4 py-2 text-sm font-semibold text-black amber-glow">
              Upgrade to Professional
            </a>
          </div>
        ) : (
          <>
            {data && (
              <div className="mt-4 flex items-center justify-between">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted" data-testid="show-log-total">
                  Total read time: <span className="text-brand-amber">{fmtDur(data.total_read_seconds)}</span>
                </div>
                <button onClick={exportCsv} disabled={exporting || data.entries.length === 0} data-testid="show-log-export-btn"
                  className="inline-flex items-center gap-1.5 rounded-md bg-brand-amber px-4 py-2 text-sm font-semibold text-black disabled:opacity-50">
                  {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Export CSV
                </button>
              </div>
            )}

            {data && data.entries.length > 0 && (
              <div className="mt-5 border-t border-line pt-4">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-2">// email this receipt to a sponsor</div>
                <div className="flex items-center gap-2">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="show-log-email-input"
                    placeholder="sponsor@company.com"
                    className="flex-1 rounded-md border border-line bg-bg-elevated px-3 py-2 text-sm outline-none focus:border-brand-amber/60"
                  />
                  <button onClick={sendEmail} disabled={sending} data-testid="show-log-email-btn"
                    className="inline-flex items-center gap-1.5 rounded-md border border-cyan-500/50 bg-cyan-500/10 px-4 py-2 text-sm font-semibold text-cyan-300 hover:bg-cyan-500/20 transition disabled:opacity-50">
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Send
                  </button>
                </div>
                <div className="mt-2 text-[11px] text-ink-muted">
                  Sends a proof-of-play report with the CSV attached — perfect sponsor receipt.
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function AddSegmentModal({ onClose, onAdd, startWithAi = false }) {
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [pool, setPool] = useState(null);
  const [aiOpen, setAiOpen] = useState(startWithAi);

  useEffect(() => {
    api.get("/prompter/available-scripts")
      .then((r) => setPool(r.data.scripts || []))
      .catch(() => setPool([]));
  }, []);

  const save = async (t, txt, source) => {
    if (!txt?.trim()) { toast.error("Script text is required"); return; }
    setSaving(true);
    try { await onAdd(t, txt, source); }
    catch { toast.error("Could not add segment"); setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="add-segment-modal">
      <div className="w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-xl border border-line bg-bg-surface p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="font-display font-bold text-lg">Add segment</div>
          <button onClick={onClose} data-testid="add-modal-close"
            className="w-7 h-7 rounded-md flex items-center justify-center border border-line text-ink-muted hover:text-ink-primary transition">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>

        <label className="block text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-1">Segment title</label>
        <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="add-title-input"
          placeholder="e.g. Weather & traffic"
          className="w-full rounded-md border border-line bg-bg-elevated px-3 py-2 text-sm outline-none focus:border-brand-amber/60 mb-3" />
        <div className="flex items-center justify-between mb-1">
          <label className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">Script</label>
          <AiWriteButton onClick={() => setAiOpen(true)} testid="add-ai-write-btn" label="Write for me" />
        </div>
        <textarea value={text} onChange={(e) => setText(e.target.value)} data-testid="add-text-input"
          rows={6} placeholder="Type or paste what you'll say on air. Use **asterisks** to highlight phrases."
          className="w-full resize-y rounded-md border border-line bg-bg-elevated px-3 py-2 text-sm outline-none focus:border-brand-amber/60" />
        <div className="mt-3 flex justify-end">
          <button onClick={() => save(title, text, "manual")} disabled={saving} data-testid="add-save-btn"
            className="inline-flex items-center gap-1.5 rounded-md bg-brand-amber px-4 py-2 text-sm font-semibold text-black disabled:opacity-50">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add to rundown
          </button>
        </div>

        <div className="mt-5 border-t border-line pt-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-2">// or pull from your recent scripts</div>
          {pool === null ? (
            <div className="text-xs text-ink-muted py-3"><Loader2 className="h-3.5 w-3.5 animate-spin inline mr-1.5" />Looking across your studios...</div>
          ) : pool.length === 0 ? (
            <div className="text-xs text-ink-muted py-3">No generated scripts found yet — create some in News Studio, Commercials or Request Line, or use "To Prompter" buttons there.</div>
          ) : (
            <div className="flex flex-col gap-1.5 max-h-56 overflow-y-auto sidebar-scroll">
              {pool.map((p, i) => {
                const tag = SOURCE_TAGS[p.source_module] || SOURCE_TAGS.manual;
                return (
                  <button key={i} onClick={() => save(p.title, p.script_text, p.source_module)} disabled={saving}
                    data-testid={`available-script-${i}`}
                    className="text-left rounded-lg border border-line bg-bg-elevated p-3 hover:border-brand-amber/50 transition disabled:opacity-50">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="flex-1 min-w-0 text-sm font-semibold truncate">{p.title}</span>
                      <span className={`shrink-0 rounded border px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider ${tag.cls}`}>{tag.label}</span>
                    </div>
                    <div className="text-xs text-ink-secondary line-clamp-2">{p.script_text}</div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <AiWriteScriptModal
          open={aiOpen}
          onClose={() => setAiOpen(false)}
          onApply={({ title: t, script }) => {
            setText(script || "");
            setTitle((cur) => cur.trim() ? cur : (t || ""));
            setAiOpen(false);
          }}
          defaultScriptType="show"
          defaultTone="conversational"
          defaultLengthSec={30}
          surface="prompter"
        />
      </div>
    </div>
  );
}
